import {CustomItem, Tabs} from '@jetbrains/ring-ui/components/tabs/tabs'
import * as React from 'react'
import {useCallback, useMemo} from 'react'
import type {EntryPointProps} from 'react-relay'
import type {PreloadedQuery} from 'react-relay/relay-hooks/EntryPointTypes'
import {useLocation, useNavigate, useParams} from 'react-router-dom'

import {EntityRedirect} from '../../../containers/EntityLink'
import {EntityProvider} from '../../../contexts/entity'
import {useBranch, useQueryParam} from '../../../hooks/routes'
import {useTabs} from '../../../hooks/useTabs'
import {getBuildTypeHasNonDefaultBranchesArg} from '../../../rest/branches'
import {getBuildTypeArg} from '../../../rest/buildTypes'
import HTTPError, {HTTPCodesEnum} from '../../../rest/errors/HTTPError'
import {getHrefWithQueryParams} from '../../../routes'
import {
  calcNextTab,
  getBuildType,
  getProject,
  getTabUrl,
  isBuildTypeLoaded,
} from '../../../selectors'
import {restApi} from '../../../services/rest'
import type {BuildTypeId, Tab, TabId} from '../../../types'
import {BuildTypePageTabNamesEnum, BuildTypePageTabTitlesEnum, stringifyId} from '../../../types'
import {stringifyBranch} from '../../../utils/branchNames'
import {emptyArray} from '../../../utils/empty'
import {extractError} from '../../../utils/extractError'
import {parseTabTitle} from '../../../utils/tabs'
import {AvailabilityErrorPage} from '../../common/AvailabilityErrorPage/AvailabilityErrorPage'
import {getAvailabilityErrorMessage} from '../../common/AvailabilityErrorPage/AvailabilityErrorPage.utils'
import ClassicUITabAdapter from '../../common/ClassicUITabAdapter/ClassicUITabAdapter'
import ErrorPage from '../../common/ErrorPage/ErrorPage'
import Loader from '../../common/Loader/Loader'
import OverviewTab, {
  createClassicUIOverviewTab,
  renderTitle,
} from '../../common/OverviewTab/OverviewTab'
import tabStyles from '../../common/OverviewTab/OverviewTab.css'
import MammothHint from '../../common/OverviewTab/OverviewTab.mammoth'
import type {PermalinksQuery} from '../../common/Permalinks/__generated__/PermalinksQuery.graphql'
import TabPlugin from '../../common/Plugin/TabPlugin/TabPlugin'
import TabsLoader from '../../common/TabsLoader/TabsLoader'

import BuildTypeChainsTab from './BuildTypeChainsTab/BuildTypeChainsTab'
import BuildTypeChangeLogTab from './BuildTypeChangeLogTab/BuildTypeChangeLogTab'
import type {BuildTypeHistoryQuery} from './BuildTypeOverviewTab/BuildTypeHistory/__generated__/BuildTypeHistoryQuery.graphql'
import BuildTypeOverviewTab from './BuildTypeOverviewTab/BuildTypeOverviewTab'
import {useRefetchBuildTypeOnPermissionsChange} from './BuildTypePage.hooks'
import BuildTypePageHeader from './BuildTypePageHeader/BuildTypePageHeader'
import PendingChangesTab from './PendingChangesTab/PendingChangesTab'

import styles from './BuildTypePage.css'

import {useAppSelector} from 'src/hooks/react-redux'

type Props = {
  buildTypeId: BuildTypeId
  buildTypeHistoryQuery?: PreloadedQuery<BuildTypeHistoryQuery>
  permalinksQuery?: PreloadedQuery<PermalinksQuery>
}
export function BuildTypePage({buildTypeId, buildTypeHistoryQuery, permalinksQuery}: Props) {
  const branch = useBranch()
  const location = useLocation()
  const navigate = useNavigate()
  const projectId = useAppSelector(state => getBuildType(state, buildTypeId)?.projectId)
  const arg = getBuildTypeArg(buildTypeId)
  const {rawError, actualId} = restApi.endpoints.getBuildTypeNormalized.useQuery(arg, {
    selectFromResult: ({error, data}) => ({rawError: error, actualId: data?.result}),
  })
  const error = extractError(rawError)
  const idChanged = actualId != null && actualId !== buildTypeId
  const {withBranches} = restApi.endpoints.checkIfBranchExistsInBuildType.useQuery(
    getBuildTypeHasNonDefaultBranchesArg(buildTypeId),
    {selectFromResult: ({data}) => ({withBranches: data})},
  )
  const projectLoaded = useAppSelector(state => Boolean(getProject(state, projectId)))
  const buildTypeLoaded = useAppSelector(state => isBuildTypeLoaded(state, buildTypeId))
  const currentTabId = useQueryParam('buildTypeTab')
  const currentTab = useAppSelector(state =>
    calcNextTab(state, {tab: currentTabId, buildTypeId, branch}),
  )
  const {
    tabs: {classicTabs, sakuraTabs},
    allTabsReady,
  } = useTabs({buildTypeId, projectId, branch})

  const onSelect = useCallback(
    (tab: TabId) => {
      navigate(
        getHrefWithQueryParams(location, null, params => ({
          buildTypeTab: tab,
          branch: params.branch,
        })),
      )
    },
    [location, navigate],
  )

  const getImplementedInSakuraUITabs = useCallback(
    (id: TabId) => {
      switch (id) {
        case BuildTypePageTabNamesEnum.OVERVIEW:
          return (
            <BuildTypeOverviewTab
              branch={branch}
              buildTypeId={buildTypeId}
              withBranches={withBranches}
              buildTypeHistoryQuery={buildTypeHistoryQuery}
              permalinksQuery={permalinksQuery}
            />
          )

        case BuildTypePageTabNamesEnum.PENDING_CHANGES:
          return <PendingChangesTab buildTypeId={buildTypeId} />

        case BuildTypePageTabNamesEnum.CHAINS:
          return <BuildTypeChainsTab />

        case BuildTypePageTabNamesEnum.CHANGE_LOG:
          return <BuildTypeChangeLogTab buildTypeId={buildTypeId} />

        default:
          return null
      }
    },
    [branch, buildTypeId, buildTypeHistoryQuery, permalinksQuery, withBranches],
  )

  const getTabContent = useCallback(
    ({
      tab,
      tabId,
      titleText,
      title,
    }: {
      tab?: Tab
      tabId: string
      titleText: string
      title: (arg0: boolean) => React.ReactElement
    }) => {
      const tabContent = getImplementedInSakuraUITabs(tabId)
      const Wrapper = tab?.sakura === true ? TabPlugin : ClassicUITabAdapter

      return (
        <OverviewTab
          key={stringifyId(tabId)}
          id={tabId}
          title={title}
          titleProps={{'aria-label': titleText}}
          isClassic={tabContent == null}
          href={getTabUrl({buildTypeId}, tabId)}
        >
          {tabContent || (
            <Wrapper
              key={stringifyBranch(branch)}
              tabId={tabId}
              title={titleText}
              projectId={projectId}
              buildTypeId={buildTypeId}
              branch={branch}
              js={tab?.js}
              css={tab?.css}
              url={tab?.url}
            />
          )}
        </OverviewTab>
      )
    },
    [branch, buildTypeId, getImplementedInSakuraUITabs, projectId],
  )

  const renderTab = useCallback(
    (tab?: Tab) => {
      let tabId = currentTab
      let titleText =
        BuildTypePageTabTitlesEnum[tabId] ??
        stringifyId(tabId).charAt(0).toUpperCase() + stringifyId(tabId).slice(1)
      let title = renderTitle(titleText)

      if (tab != null) {
        const {name, counter} = parseTabTitle(tab.title)
        tabId = tab.id
        titleText = BuildTypePageTabTitlesEnum[tabId] ?? name
        title = renderTitle(titleText, counter)
      }

      if (tab?.onlyClassicUI === true) {
        return createClassicUIOverviewTab({id: tabId, title: titleText, href: tab.href})
      }

      return getTabContent({title, tab, tabId, titleText})
    },
    [currentTab, getTabContent],
  )

  const classicUITabs = useMemo(
    () => (classicTabs.length ? classicTabs.map(renderTab) : emptyArray),
    [renderTab, classicTabs],
  )
  const sakuraUITabs = useMemo(
    () => (sakuraTabs.length ? sakuraTabs.map(renderTab) : [renderTab()]),
    [sakuraTabs, renderTab],
  )

  const [fetchSingleBuildTypeData] =
    restApi.endpoints.getBuildTypeNormalized.useLazyQuerySubscription()

  if (idChanged) {
    return <EntityRedirect buildTypeId={actualId} redirectedFromId={buildTypeId} />
  }

  if (!window.GLOBAL_ERROR_PAGE_DISABLED && error != null) {
    return (
      <ErrorPage
        error={error}
        buildTypeId={buildTypeId}
        onRetry={
          error instanceof HTTPError && error.statusCode === HTTPCodesEnum.FORBIDDEN
            ? null
            : () => fetchSingleBuildTypeData(arg)
        }
      />
    )
  }

  if (!buildTypeLoaded || !projectLoaded) {
    return <Loader className={styles.loader} />
  }

  return (
    <EntityProvider buildTypeId={buildTypeId}>
      <BuildTypePageHeader projectId={projectId} buildTypeId={buildTypeId} />
      <Tabs
        className={tabStyles.tabs}
        data-test="overview-tabs-panel"
        selected={currentTab}
        onSelect={onSelect}
        autoCollapse={process.env.NODE_ENV !== 'test'}
        initialVisibleItems={3}
        morePopupBeforeEnd={(classicTabs.some(tab => tab.onlyClassicUI) && <MammothHint />) || null}
        morePopupItemClassName={styles.morePopupItemClassName}
      >
        {sakuraUITabs}
        {classicUITabs}
        {!allTabsReady && (
          <CustomItem key="tabs_loader">
            <TabsLoader />
          </CustomItem>
        )}
      </Tabs>
    </EntityProvider>
  )
}

export type Queries = {buildTypeHistory?: BuildTypeHistoryQuery; permalinks?: PermalinksQuery}

export default function BuildTypePageContainer({queries}: EntryPointProps<Queries, {}, {}, {}>) {
  const {buildTypeId} = useParams()
  useRefetchBuildTypeOnPermissionsChange(buildTypeId)
  return (
    <AvailabilityErrorPage
      getErrorMessage={error => getAvailabilityErrorMessage({error, buildTypeId})}
    >
      <BuildTypePage
        key={buildTypeId}
        buildTypeId={buildTypeId!}
        buildTypeHistoryQuery={
          queries.buildTypeHistory as PreloadedQuery<BuildTypeHistoryQuery> | undefined
        }
        permalinksQuery={queries.permalinks as PreloadedQuery<PermalinksQuery> | undefined}
      />
    </AvailabilityErrorPage>
  )
}

export type BuildTypePageContainerComponent = typeof BuildTypePageContainer
